import React, { useState, useContext, useCallback } from "react";
import { CartContext } from "../pages/CartPage/CartContext";

/**
 * TechProductSection Component
 * Use this for showcasing tech gadget categories (Laptops, Smartphones, Accessories, etc.)
 * 
 * Props:
 * - products: array of products to display
 * - navigate: navigation function
 * - title: Section title (e.g., "Smartphones", "Laptops")
 * - subtitle: Optional subtitle
 * - categoryColor: Color theme (e.g., "blue", "purple", "indigo")
 */


const TechProductSection = ({ 
  products = [], 
  navigate, 
  title = "Tech Products",
  subtitle = "Featured Tech Gadgets",
  categoryColor = "blue"
}) => {
  const { addToCart } = useContext(CartContext);
  const [animatedButtonId, setAnimatedButtonId] = useState(null);
  const [flyingItem, setFlyingItem] = useState(null);

  const colorClasses = {
    blue: { btn: "from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700", accent: "text-blue-600", bg: "from-blue-50 to-blue-100", badge: "bg-blue-100 text-blue-700" },
    purple: { btn: "from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700", accent: "text-purple-600", bg: "from-purple-50 to-purple-100", badge: "bg-purple-100 text-purple-700" },
    indigo: { btn: "from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700", accent: "text-indigo-600", bg: "from-indigo-50 to-indigo-100", badge: "bg-indigo-100 text-indigo-700" },
    cyan: { btn: "from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700", accent: "text-cyan-600", bg: "from-cyan-50 to-cyan-100", badge: "bg-cyan-100 text-cyan-700" },
  };

  const colors = colorClasses[categoryColor] || colorClasses.blue;

  const handleAddToCart = useCallback((product, event) => {
    const productId = product._id || product.id;
    const cartProduct = {
      id: productId,
      name: product.name,
      price: Number(product.price || 0),
      image: product.image || product.imageUrl || "",
      description: product.description || "",
      categoryId: product.categoryId || product.category || "",
      checkinDT: !!product.checkinDT,
      multiDayDiscountDays: Number(product.multiDayDiscountDays || 0),
      multiDayDiscountPercent: Number(product.multiDayDiscountPercent || 0),
      quantity: 1,
    };
    addToCart(cartProduct);
    setAnimatedButtonId(productId);
    setTimeout(() => setAnimatedButtonId(null), 300);

    const buttonRect = event.target.getBoundingClientRect();
    const cartIcons = document.querySelectorAll("[data-cart-icon]");
    let cartIcon = null;
    cartIcons.forEach((icon) => {
      const rect = icon.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) cartIcon = icon;
    });
    if (cartIcon) {
      const cartRect = cartIcon.getBoundingClientRect();
      setFlyingItem({
        id: Date.now(),
        startX: buttonRect.left + buttonRect.width / 2,
        startY: buttonRect.top + buttonRect.height / 2,
        deltaX: cartRect.left + cartRect.width / 2 - (buttonRect.left + buttonRect.width / 2),
        deltaY: cartRect.top + cartRect.height / 2 - (buttonRect.top + buttonRect.height / 2),
      });
      setTimeout(() => setFlyingItem(null), 800);
    }
  }, [addToCart]);

  const goToDetail = (product) => {
    if (typeof navigate === "function") navigate("productDetail", product._id || product.id);
  };

  if (!products || products.length === 0) return null;

  return (
    <section className={`container mx-auto px-4 mb-12 mt-6`}>
      {/* Section Header */}
      <div className="text-center mb-8">
        <span className={`text-xs font-bold ${colors.accent} uppercase tracking-wider`}>Best Collection</span>
        <h2 className="text-3xl font-extrabold text-gray-900 mt-1 mb-2">{title}</h2>
        <p className="text-gray-600 text-sm max-w-2xl mx-auto">{subtitle}</p>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 relative">
        {products.slice(0, 8).map((product) => {
          const price = Number(product.price || 0);
          const mrp = Number(product.mrp || 0);
          const showMrp = mrp > 0 && mrp > price;
          const discountPercent = showMrp ? Math.round(((mrp - price) / mrp) * 100) : 0;
          const productId = product._id || product.id;

          return (
            <div
              key={productId}
              className="group bg-white rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-300 overflow-visible border border-gray-50 hover:border-blue-100 hover:z-20 relative"
            >
              {/* Image */}
              <div
                className={`relative h-48 overflow-hidden cursor-pointer bg-gradient-to-br ${colors.bg} rounded-t-2xl flex items-center justify-center`}
                onClick={() => goToDetail(product)}
              >
                {showMrp && discountPercent > 0 && (
                  <div className="absolute top-3 left-3 z-10 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-md">
                    {discountPercent}% OFF
                  </div>
                )}
                {product.image || product.imageUrl ? (
                  <img
                    src={product.image || product.imageUrl}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://placehold.co/400x300/E0E7FF/4F46E5?text=${(product.name || "Product").replace(/\s/g, "+")}`;
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-5xl">💻</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <h3
                  className="font-bold text-gray-900 text-sm mb-1 line-clamp-1 cursor-pointer hover:text-blue-600 transition-colors"
                  onClick={() => goToDetail(product)}
                >
                  {product.name}
                </h3>
                <p className="text-xs text-gray-500 mb-2 line-clamp-2">{product.description}</p>

                {/* Rating placeholder */}
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                  <span className="text-xs text-gray-400 ml-1">4.5</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-end gap-2">
                    {showMrp && (
                      <span className="text-sm text-gray-400 line-through">₹{mrp.toFixed(0)}</span>
                    )}
                    <span className={`text-lg font-extrabold ${colors.accent}`}>₹{price.toFixed(0)}</span>
                  </div>

                  <button
                    onClick={(e) => handleAddToCart(product, e)}
                    className={`bg-gradient-to-r ${colors.btn} text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-1 flex-shrink-0 ${
                      animatedButtonId === productId ? "scale-110" : ""
                    }`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    Add
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Flying cart animation */}
      {flyingItem && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            left: flyingItem.startX,
            top: flyingItem.startY,
            transform: `translate(${flyingItem.deltaX}px, ${flyingItem.deltaY}px)`,
            animation: "flyToCartTech 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards",
          }}
        >
          <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-xl shadow-lg">
            🛒
          </div>
        </div>
      )}

      <style>{`
        @keyframes flyToCartTech {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          50% { opacity: 0.8; }
          100% { transform: translate(var(--delta-x), var(--delta-y)) scale(0.3); opacity: 0; }
        }
      `}</style>
    </section>
  );
};

export default TechProductSection;
